<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { AffiliateConfig } from '~/utils/perfilOficialConfigs';

const props = defineProps<{
  affiliateName: string;
  config: AffiliateConfig;
  loading?: 'lazy' | 'eager';
}>();

const router = useRouter();

const displayName = computed(() => {
  return props.config.affiliateDetails?.name || props.affiliateName;
});

const avatarUrl = computed(() => {
  return props.config.affiliateDetails?.avatarUrl || '';
});

const avatarVariant = computed(() => {
  return props.config.affiliateDetails?.avatarVariant ?? 'circle';
});

const badge = computed(() => {
  return props.config.affiliateDetails?.title || '';
});

const categories = computed(() => {
  return props.config.categories;
});

const cardAccentColor = computed(() => {
  return props.config.accentColor || '#00C9FF';
});

const cardAccentColorRgb = computed(() => {
  const hex = cardAccentColor.value.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `${r},${g},${b}`;
});

const cardAvatarGradient = computed(() => {
  const color = cardAccentColor.value;

  return `radial-gradient(65.23% 65.23% at 50.31% 53.73%, #FFF 0%, ${color} 45.19%, ${color}80 100%)`;
});

// Drag functionality variables
const categoriesContainer = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);
const dragged = ref(false);
const isHoveringButton = ref(false);
const isClickingButton = ref(false);
const profileUrlSlug = computed(
  () => props.config.urlSlug || props.affiliateName,
);

const goToProfile = () => {
  isClickingButton.value = true;

  setTimeout(() => {
    router.push(`/perfil-oficial/${profileUrlSlug.value}`);
  }, 100);
};

// Drag functionality functions
function onMouseDown(e: MouseEvent) {
  if (!categoriesContainer.value) return;
  isDragging.value = true;
  startX.value = e.pageX - categoriesContainer.value.offsetLeft;
  scrollLeft.value = categoriesContainer.value.scrollLeft;
  categoriesContainer.value.style.cursor = 'grabbing';
  categoriesContainer.value.style.userSelect = 'none';
  dragged.value = false;
}

function onMouseMove(e: MouseEvent) {
  if (!isDragging.value || !categoriesContainer.value) return;
  e.preventDefault();

  const x = e.pageX - categoriesContainer.value.offsetLeft;
  const walk = x - startX.value;

  if (Math.abs(walk) > 5) {
    dragged.value = true;
  }
  categoriesContainer.value.scrollLeft = scrollLeft.value - walk * 1;
}

function onMouseUp() {
  if (!categoriesContainer.value) return;
  isDragging.value = false;
  categoriesContainer.value.style.cursor = 'grab';
  categoriesContainer.value.style.userSelect = 'auto';
  if (dragged.value) {
    setTimeout(() => {
      dragged.value = false;
    }, 50);
  }
}

function onMouseLeave() {
  if (!categoriesContainer.value) return;
  isDragging.value = false;
  categoriesContainer.value.style.cursor = 'grab';
  categoriesContainer.value.style.userSelect = 'auto';
}

function handleCategoryClick(event: MouseEvent) {
  if (dragged.value) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    dragged.value = false;
  }
}
</script>

<template>
  <div
    class="affiliate-card flex flex-col justify-end items-center gap-2.5 bg-gradient-to-b from-[#001A4080] to-[#001A40] p-[15px] rounded-[10px] w-[180px] select-none border-2 border-solid border-[#001A40]"
  >
    <!-- Avatar redondo (perfis oficiais comuns) -->
    <div
      v-if="avatarVariant !== 'icon'"
      class="avatar-container w-24 h-24 rounded-full border-2 border-solid overflow-hidden flex-shrink-0 cursor-pointer"
      :style="{
        borderColor: cardAccentColor,
        background: cardAvatarGradient,
      }"
      @click="goToProfile"
    >
      <NuxtImg
        v-if="avatarUrl"
        alt="Avatar"
        class="avatar-image object-center"
        draggable="false"
        :loading="props.loading || 'lazy'"
        :src="avatarUrl"
      />
      <div
        v-else
        class="avatar-image w-full h-full flex items-center justify-center text-white/50 text-2xl font-bold"
      >
        {{ displayName.charAt(0).toUpperCase() }}
      </div>
    </div>

    <!-- Avatar como ícone livre (ex.: taça da Copa) -->
    <div
      v-else
      class="w-24 h-24 flex-shrink-0 cursor-pointer flex items-center justify-center"
      @click="goToProfile"
    >
      <NuxtImg
        v-if="avatarUrl"
        alt="Avatar"
        class="w-full h-full object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.5)]"
        draggable="false"
        :loading="props.loading || 'lazy'"
        :src="avatarUrl"
      />
    </div>

    <!-- Name and Badge -->
    <div class="flex items-center gap-2.5">
      <p
        class="text-white text-center text-[12px] md:text-[14px] font-bold leading-5"
        >{{ displayName }}</p
      >
      <NuxtIcon
        v-if="badge"
        class="text-primary text-[20px]"
        :filled="true"
        name="perfil-oficial/embaixador-oficial"
      />
    </div>

    <!-- View Profile Button -->
    <button
      class="flex justify-center items-center gap-[7px] self-stretch border [background:var(--ATNB---ORANGE,radial-gradient(168.3%_138%_at_36.76%_-16.67%,#FF704A_0%,#FF3400_100%))] p-2.5 rounded-md border-solid border-[#FC9984]"
      @click="goToProfile"
      @mouseleave="isHoveringButton = false"
      @mouseover="isHoveringButton = true"
    >
      <span class="text-white text-center text-xs font-extrabold leading-3"
        >VER PERFIL</span
      >
    </button>

    <!-- Categories -->
    <div
      ref="categoriesContainer"
      class="categories-container flex gap-2 overflow-x-auto whitespace-nowrap max-w-full"
      @mousedown="onMouseDown"
      @mouseleave="onMouseLeave"
      @mousemove="onMouseMove"
      @mouseup="onMouseUp"
    >
      <span
        v-for="category in categories"
        :key="category"
        class="flex justify-center items-center gap-2.5 px-2.5 py-0.5 rounded-[100px]"
        :style="{ background: `rgba(${cardAccentColorRgb},0.10)` }"
        @click="handleCategoryClick"
      >
        <span
          class="text-center text-xs font-medium leading-[15px]"
          :style="{ color: cardAccentColor }"
          >{{ category }}</span
        >
      </span>
    </div>
  </div>
</template>

<style scoped>
.affiliate-card {
  min-height: 200px;
}

.categories-container {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  cursor: grab;
}

.categories-container::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}
</style>
